
import Component, { mixins } from 'vue-class-component';
import { namespace } from 'vuex-class';
import ZccContractStore from '@/store/modules/zccContract';
import Breadcrumb, { IBreadcrumb } from '@/components/Breadcrumb.vue';
import { getClickWrapId } from '@/utils/clickWrapUtils';
import SessionMixin from '@/mixins/session';
// @ts-expect-error:
import customerDataService from '@platform/customer-data-service';
import SalesForceContract from '@/services/SalesForceContract';
import SelectPlanCard from './components/SelectPlanCard.vue';
import PlanSummaryCard from './components/PlanSummaryCard.vue';
import { AVAILABLE_PLANS, AVAILABLE_CHANNELS, PLANS_CHANNELS_COMBINATIONS } from '../../utils/plans';
import ChannelPlanDetails from './components/ChannelPlanDetails.vue';
import { IContractZcc } from '@/types/contract-zcc';

const zccContractNamespace = namespace('zccContract');

const ALLOWED_ORIGINS = ['expansion_machine', 'partnerships', 'spontaneous', 'plg', 'growth unlock', 'cart_recovery'];

enum PlanEnum {
  STARTER = 1,
  SPECIALIST = 2,
  EXPERT = 3,
  PROFESSIONAL = 4,
  ENTERPRISE = 5
}

@Component({
  components: {
    Breadcrumb,
    SelectPlanCard,
    PlanSummaryCard,
    ChannelPlanDetails,
  },
})

export default class ChangePlanZCCView extends mixins(SessionMixin) {
  salesForceContractClass: SalesForceContract | undefined;

  public get breadcrumbs(): IBreadcrumb[] {
    return [
      {
        label: `${this.$t('CHANGE_PLAN_ZCC.HEADER.BREADCRUMBS.CONFIGS')}`,
      },
      {
        label: `${this.$t('CHANGE_PLAN_ZCC.HEADER.BREADCRUMBS.PLANS')}`,
        to: '/plans-and-contracts',
      },
      {
        label: this.$tc('CHANGE_PLAN_ZCC.HEADER.BREADCRUMBS.CHANGE_PLAN', 1).toString(),
      },
    ];
  }

  @zccContractNamespace.Getter
  public zccContract!: ZccContractStore;

  private selectedType = 'software';

  private defaultSelectedPlan: any = {};

  private defaultSelectedChannel: any = {};

  private softwarePlanSelected: any = {};

  private channelPlanSelected: any = {};

  private softwarePlanData = {};

  private channelPlanData = {};

  private isChangePlanButtonLoading = false;

  private isLoading = false;

  private legalData : any = '';

  private isSoftwareCardValid = false;

  private isChannelCardValid = false;

  private hasValidated = false;

  private origin!: string;

  @zccContractNamespace.Mutation
  public setZccContract!: (contract: ZccContractStore) => void;

  onCardCliked(type: string, item: any) {
    this.selectedType = type;
    if (type === 'software') {
      this.softwarePlanSelected = item;
      this.isSoftwareCardValid = true;
    }

    if (type === 'channel') {
      this.channelPlanSelected = item;
      this.isChannelCardValid = true;
    }
  }

  formatAssignmentDate(date: string) {
    const formattedDate = new Date(date);
    return formattedDate.toISOString().slice(0, 19).replace('T', ' ');
  }

  isDowngrade() {
    const currentPlanCombination = PLANS_CHANNELS_COMBINATIONS.find(
      (combination) => combination.planId === this.zccContract.planId,
    );

    if (currentPlanCombination?.softwarePlanId) {
      return currentPlanCombination.softwarePlanId > this.softwarePlanSelected.id;
    }

    return false;
  }

  async onTermAgree(agreedOn: string, clientUserId: string) {
    const baseUri = process.env.VUE_APP_CUSTOMER_ORDER_MANAGER;
    const endpoints = {
      default: `${baseUri}/v1/selfservice/plan/request-change`,
      enterprise: `${baseUri}/v1/plans/request-enterprise`,
    };

    let status = 'success';
    let message = 'SUCCESS';

    try {
      if (this.softwarePlanSelected.name === 'Enterprise') {
        await this.requestEnterpriseChange(endpoints.enterprise);
      } else {
        await this.requestPlanChange(endpoints.default, agreedOn, clientUserId);
      }
    } catch (error) {
      message = 'ERROR';
      status = 'danger';
    }

    this.redirectWithFeedback();
  }

  private async requestEnterpriseChange(uri: string) {
    await this.$http.post(uri, {
      contractNumber: this.zccContract.salesforceContractId,
      fromPlan: this.defaultSelectedPlan.name,
      taxId: this.legalData.document,
    });
  }

  private async requestDowngrade(uri: string) {
    await this.$http.post(uri, {
      contractNumber: this.zccContract.salesforceContractId,
      fromPlan: this.defaultSelectedPlan.name,
      toPlan: this.softwarePlanSelected.name,
      taxId: this.legalData.document,
    });
  }

  private async requestPlanChange(uri: string, agreedOn: string, clientUserId: string) {
    const planId = PLANS_CHANNELS_COMBINATIONS.find(
      (combination) => combination.channelPlanId === this.channelPlanSelected.id
            && combination.softwarePlanId === this.softwarePlanSelected.id,
    )?.planId;

    await this.$http.post(uri, {
      planId,
      channel: this.channelPlanSelected.id,
      'z-origin': this.origin,
    });
  }

  private redirectWithFeedback() {
    this.$router.push({ path: '/plans-and-contracts', query: { requestedPlanChange: 'requested' } });
  }

  mountPlanData() {
    this.channelPlanData = {
      title: this.$t('CHANGE_PLAN_ZCC.SELECT_PLAN_CARD.CHANNEL_PLAN_TITLE'),
      type: 'channel',
      currency: this.zccContract.currency,
      data: AVAILABLE_CHANNELS[this.zccContract.currency as keyof typeof AVAILABLE_CHANNELS],
    };

    this.softwarePlanData = {
      title: this.$t('CHANGE_PLAN_ZCC.SELECT_PLAN_CARD.SOFTWARE_PLAN_TITLE'),
      type: 'software',
      currency: this.zccContract.currency,
      data: AVAILABLE_PLANS[this.zccContract.currency as keyof typeof AVAILABLE_PLANS],
    };
  }

  created() {
    this.salesForceContractClass = new SalesForceContract(this);
  }

  async mounted() {
    this.origin = this.resolveOrigin();
    this.isLoading = true;

    this.legalData = await customerDataService.getLegalData();

    if (
      !this.zccContract
      || !this.zccContract.planId
      || !this.zccContract.currency
      || !this.zccContract.zenviaSubscriptionCode
      || !this.zccContract.country
      || !this.zccContract.status
      || !this.zccContract.salesforceContractId
      || this.zccContract.status === 'ACTIVE'
    ) {
      try {
        const { data } = await this.$http.get<IContractZcc>(`${process.env.VUE_APP_PAYMENT_HISTORY_URL}/v1/zcc-subscription`);

        // eslint-disable-next-line no-unused-expressions
        data.status === 'ACTIVE' ? this.setZccContract({
          planId: data.plan.id,
          currency: data.currency,
          zenviaSubscriptionCode: data.zenviaSubscriptionCode,
          country: data.country,
          status: data.status,
          salesforceContractId: data.salesforceContractId,
        } as ZccContractStore)
          : this.$router.push('/plans-and-contracts');

        if (this.zccContract?.changePlanData === undefined) {
          await this.salesForceContractClass?.getContractHandler(data?.salesforceContractId);
        }

        if (this.zccContract?.changePlanData?.status === 'Em andamento') {
          this.$router.push('/plans-and-contracts');
        }
      } catch (e) {
        console.error('ZCC Contract fetch failed', e);
      }
    }
    const planCombination = PLANS_CHANNELS_COMBINATIONS.find((combination) => combination.planId === this.zccContract.planId);
    if (planCombination?.softwarePlanId && planCombination.channelPlanId) {
      this.defaultSelectedPlan = AVAILABLE_PLANS[this.zccContract.currency as keyof typeof AVAILABLE_PLANS].find((plan) => plan.id === Number(planCombination?.softwarePlanId));
      this.defaultSelectedChannel = AVAILABLE_CHANNELS[this.zccContract.currency as keyof typeof AVAILABLE_CHANNELS].find((channel) => channel.id === Number(planCombination?.channelPlanId));
    }

    this.softwarePlanSelected = this.defaultSelectedPlan;
    this.channelPlanSelected = this.defaultSelectedChannel;
    this.mountPlanData();

    this.isLoading = false;
  }

  private resolveOrigin(): string {
    const origin = this.$route.query['z-origin'] as string;

    if (!origin) {
      return 'unidentified';
    }

    return ALLOWED_ORIGINS.includes(origin) ? origin : 'unmapped';
  }

  handleSubmitPlanChange() {
    this.hasValidated = true;

    if (!this.softwarePlanSelected?.id || !this.channelPlanSelected?.id) {
      this.isSoftwareCardValid = !!this.softwarePlanSelected?.id;
      this.isChannelCardValid = !!this.channelPlanSelected?.id;
      return;
    }

    this.isSoftwareCardValid = true;
    this.isChannelCardValid = true;

    this.renderClickWrapModal();
  }

  renderClickWrapModal() {
    this.isChangePlanButtonLoading = true;
    (window as any).docuSignClick.Clickwrap.render({
      environment: process.env.VUE_APP_DOCUSIGN_ENVIRONMENT_URL,
      accountId: process.env.VUE_APP_DOCUSIGN_ACCOUNT_ID,
      clickwrapId: getClickWrapId(this.zccContract.country),
      clientUserId: this.mxSession_user.email,
      documentData: {
        fullName: this.mxSession_user.name,
        email: this.mxSession_user.email,
        company: this.mxSession_organization.name,
      },
      style: {
        agreeButton: {
          color: 'white',
          backgroundColor: '#5a4cfb',
          width: '100%',
          borderRadius: '0px',
        },
        container: {
          borderRadius: '10px',
        },
      },
      onAgreed: (response: any) => {
        this.onTermAgree(response.agreedOn, response.clientUserId);
        this.isChangePlanButtonLoading = false;
      },
      onMustAgree: () => {
        this.isChangePlanButtonLoading = false;
      },
    }, '#clickwrap-terms-of-service');
  }

  get isEnterprisePlan() {
    return this.softwarePlanSelected?.id === PlanEnum.ENTERPRISE;
  }

  get hasPreSelectedPlan() {
    return this.defaultSelectedPlan?.id && this.defaultSelectedChannel?.id;
  }
}
